import React, { useState, useEffect } from 'react';
import { useSetCurrentPage, useUser } from '../stores/useAppStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import GlassCard from '../components/ui/GlassCard';
import TelegramAvatar from '../components/ui/TelegramAvatar';
import ScrollReveal from '../components/ui/ScrollReveal';
import { stockService, MarketIndex } from '../services/stockService';
import { MarketSentimentCard } from '../components/Stock/MarketSentiment/MarketSentimentCard';

const StockDashboard: React.FC = () => {
  const setCurrentPage = useSetCurrentPage();
  const user = useUser();
  const [indices, setIndices] = useState<MarketIndex[]>([
    { name: '上证指数', value: '...', changePercent: 0, isUp: true, symbol: '', changeAmount: 0 },
    { name: '深证成指', value: '...', changePercent: 0, isUp: true, symbol: '', changeAmount: 0 },
    { name: '创业板指', value: '...', changePercent: 0, isUp: true, symbol: '', changeAmount: 0 },
  ]);

  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        const indicesData = await stockService.getMarketIndices();
        if (indicesData && indicesData.length > 0) {
          setIndices(indicesData);
        }
      } catch (error) {
        console.error('Failed to fetch market indices:', error);
      }
    };

    fetchMarketData();
    const timer = setInterval(fetchMarketData, 30000); // 30秒更新一次

    return () => clearInterval(timer);
  }, []);

  // 功能模块配置
  const modules = [
    {
      id: 'monitor',
      title: '智能监控',
      desc: 'AI 实时盯盘',
      icon: '📡',
      color: 'from-blue-500 to-cyan-500',
      action: () => setCurrentPage('stock-monitors'),
    },
    {
      id: 'signals',
      title: '核心信号',
      desc: '量价齐升扫板',
      icon: '🔥',
      color: 'from-red-500 to-orange-500',
      action: () => setCurrentPage('stock-signals'),
    },
    {
      id: 'strategy',
      title: '选股策略',
      desc: '多因子量化选股',
      icon: '🎯',
      color: 'from-purple-500 to-pink-500',
      action: () => setCurrentPage('stock-strategies'),
    },
    {
      id: 'quant-portfolio',
      title: '量化组合',
      desc: '15只优化标的',
      icon: '💎',
      color: 'from-emerald-500 to-teal-500',
      action: () => setCurrentPage('stock-quant-portfolio'),
    },
    {
      id: 'portfolio',
      title: '股票组合',
      desc: '我的持仓管理',
      icon: '💼',
      color: 'from-amber-500 to-yellow-500',
      action: () => setCurrentPage('stock-portfolio'),
    },
    {
      id: 'research-reports',
      title: 'AI 深度研报',
      desc: '每日个股精选',
      icon: '📝',
      color: 'from-blue-600 to-indigo-600',
      action: () => setCurrentPage('research-reports'),
    },
    {
      id: 'backtest',
      title: '回测分析',
      desc: '历史数据验证',
      icon: '📊',
      color: 'from-orange-500 to-red-500',
      action: () => console.log('Backtest clicked'),
    },
    {
      id: 'settings',
      title: '系统设置',
      desc: '参数配置与管理',
      icon: '⚙️',
      color: 'from-gray-500 to-slate-500',
      action: () => console.log('Settings clicked'),
    },
  ];

  return (
    <GradientBackground className="pb-20">
      <ParticleBackground />

      <div className="pt-6 px-4 space-y-6 relative z-10">
        {/* 顶部栏：个人信息与切换回Crypto */}
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            <TelegramAvatar
              photoUrl={user?.photo_url}
              firstName={user?.first_name}
              lastName={user?.last_name}
              username={user?.username}
              size="md"
              showStatus={false}
            />
            <div>
              <h2 className="text-white font-bold text-lg">A股量化终端</h2>
              <p className="text-white/50 text-xs">AI Quant Terminal</p>
            </div>
          </div>

          <button
            onClick={() => setCurrentPage('dashboard')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-full text-xs text-white/80 border border-white/10 transition-all backdrop-blur-sm"
          >
            <span>🔄</span>
            <span>返回加密货币</span>
          </button>
        </div>

        {/* 市场指数概览 */}
        <ScrollReveal animation="fadeInUp" delay={100}>
          <div className="grid grid-cols-3 gap-2">
            {indices.map((index, i) => (
              <GlassCard key={i} className="p-3 text-center flex flex-col justify-center items-center">
                <p className="text-white/60 text-xs mb-1">{index.name}</p>
                <div className={`text-sm font-bold mb-0.5 ${index.isUp ? 'text-red-400' : 'text-green-400'}`}>
                  {index.value}
                </div>
                <div className={`text-xs ${index.isUp ? 'text-red-400/80' : 'text-green-400/80'}`}>
                  {index.changePercent > 0 ? '+' : ''}
                  {index.changePercent}%
                </div>
              </GlassCard>
            ))}
          </div>
        </ScrollReveal>

        {/* 市场情绪卡片 */}
        <ScrollReveal animation="fadeInUp" delay={150}>
          <MarketSentimentCard />
        </ScrollReveal>

        {/* 功能模块网格 */}
        <div className="grid grid-cols-2 gap-4">
          {modules.map((module, index) => (
            <ScrollReveal key={module.id} animation="fadeInUp" delay={200 + index * 100}>
              <div
                onClick={module.action}
                className="group relative overflow-hidden rounded-2xl bg-white/5 border border-white/10 p-4 hover:bg-white/10 transition-all cursor-pointer active:scale-95"
              >
                <div
                  className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${module.color} opacity-10 rounded-bl-full group-hover:scale-110 transition-transform`}
                />

                <div className="relative z-10">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-2xl mb-3 group-hover:scale-110 transition-transform">
                    {module.icon}
                  </div>
                  <h3 className="text-white font-bold text-lg mb-1">{module.title}</h3>
                  <p className="text-white/50 text-xs">{module.desc}</p>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>

        {/* 底部导航占位 (如果需要) */}
        <div className="h-10" />
      </div>
    </GradientBackground>
  );
};

export default StockDashboard;
